import { getRequiredInspectionArea } from 'features/libra/inspectionPlanning/planningStrategy'
import {
  PlanningStrategies,
  planningStrategiesSchema,
  sourceZonesSchema,
} from 'features/libra/inspectionPlanning/planningTypes'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { LibraWorkflowGlobalContext } from 'features/libra/workflow/workflowStepsApi'
import { z } from 'zod'

import {
  InsulationPreparation,
  insulationPreparationSchema,
  InsulationPreparationSection,
} from './insulationPreparationTypes'

export type InsulationPreparationRow = InsulationPreparation & {
  title: string
  estimatedRemovalAreaM2: number | undefined
}

const insulationPreparationRowsSchema = z
  .object({
    zones: sourceZonesSchema,
    preparation: insulationPreparationSchema.array(),
    strategies: planningStrategiesSchema,
  })
  .transform(({ zones, preparation, strategies }) =>
    getInsulationPreparationRows(zones, preparation, strategies)
  )

export function readInsulationPreparationRows(
  config: InsulationPreparationSection,
  fieldValues: FieldValues,
  globalContext: LibraWorkflowGlobalContext
) {
  return insulationPreparationRowsSchema.safeParse({
    zones: unwrapFieldValue(fieldValues[config.sourceZonesField]) ?? [],
    preparation: unwrapFieldValue(fieldValues[config.field]) ?? [],
    strategies: globalContext[config.strategiesContextKey],
  })
}

export function getInsulationPreparationRows(
  zones: readonly ZoneParams[],
  preparation: readonly InsulationPreparation[],
  strategies: PlanningStrategies
): InsulationPreparationRow[] {
  return zones.map((zone) => {
    const saved = preparation.find((item) => item.sourceZoneId === zone.id)
    return {
      ...saved,
      sourceZoneId: zone.id,
      title: zone.title,
      estimatedRemovalAreaM2:
        saved?.insulated === true
          ? getRequiredInspectionArea(zone, strategies)
          : undefined,
    }
  })
}

export function getPlannedInsulationRemoval(row: InsulationPreparationRow) {
  if (row.insulated === false) return 0
  if (row.insulated === undefined) return undefined
  return row.plannedRemovalAreaM2 ?? row.estimatedRemovalAreaM2
}

export function isInsulationPreparationComplete(
  rows: readonly InsulationPreparationRow[]
) {
  return (
    rows.length > 0 &&
    rows.every((row) => {
      const planned = getPlannedInsulationRemoval(row)
      return row.insulated === false || (planned !== undefined && planned > 0)
    })
  )
}

export function updateInsulationPreparation(
  preparation: readonly InsulationPreparation[],
  sourceZoneId: string,
  update: Partial<Omit<InsulationPreparation, 'sourceZoneId'>>
): InsulationPreparation[] {
  const existing = preparation.find(
    (item) => item.sourceZoneId === sourceZoneId
  )
  const next = { ...existing, ...update, sourceZoneId }
  return existing
    ? preparation.map((item) =>
        item.sourceZoneId === sourceZoneId ? next : item
      )
    : [...preparation, next]
}
